import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Briefcase, ArrowRight, GraduationCap } from 'lucide-react';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#1a1a1a]">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex justify-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_talent-nexus-37/artifacts/rmnir4c5_Nexor%20-%20sans%20fond%20blanc%20-%20png.png"
                alt="Nexor Talents"
                className="h-48 md:h-56 lg:h-64 w-auto"
              />
            </div>

            <h2 className="text-2xl md:text-3xl font-semibold text-gold mb-8">
              Bienvenue chez Nexor Talents
            </h2>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight max-w-5xl mx-auto">
              Là où les connexions humaines
              <br />
              <span className="text-gold">propulsent les carrières et les entreprises</span>
            </h1>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gold rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl md:text-3xl text-gray-800 leading-relaxed tracking-wide" style={{ lineHeight: '1.8' }}>
            Parce que derrière chaque entreprise performante se trouvent des talents exceptionnels
            et derrière chaque CV une histoire unique. Notre mission est double :
            créer les connexions qui font la différence par le{' '}
            <span className="text-gold font-semibold">recrutement</span>{' '}
            et développer le plein potentiel de chacun par la{' '}
            <span className="text-gold font-semibold">formation</span>.
          </p>
        </div>
      </section>

      {/* Nexi & Élyor - Vision */}
      <section className="py-8 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/images/mascots/vision.png"
            alt="Nexi et Élyor regardent vers l'avenir"
            className="w-full max-w-2xl mx-auto"
          />
        </div>
      </section>

      {/* Nos Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Nos services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deux piliers pour accompagner votre réussite professionnelle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-10">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 mb-6">
                  <Briefcase className="w-10 h-10 text-gold" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Recrutement</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Chasse de têtes spécialisée en assurance de dommages. Nous connectons les bons talents aux bons employeurs, avec précision et engagement.
                </p>
                <Link to="/recrutement" className="text-gold font-semibold hover:underline inline-flex items-center gap-1">
                  En savoir plus <ArrowRight className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-10">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 mb-6">
                  <GraduationCap className="w-10 h-10 text-gold" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Formation</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Formations en expertise assurance et coaching carrière pour développer vos compétences et propulser votre trajectoire professionnelle.
                </p>
                <Link to="/formations" className="text-gold font-semibold hover:underline inline-flex items-center gap-1">
                  Découvrir nos formations <ArrowRight className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final Statement */}
      <section className="py-20 bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gold">
            Nexor Talents
          </h3>
          <p className="text-2xl text-gray-300">
            Là où les parcours prennent vie. Là où les équipes prennent de la hauteur.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
